// src/components/NewVehicleTabPricing.tsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VehicleVariant } from '../types/NewVehicleTypes';

interface NewVehicleTabPricingProps {
  variants: VehicleVariant[];
  selectedVariantIndex?: number;
}

const NewVehicleTabPricing: React.FC<NewVehicleTabPricingProps> = ({ 
  variants, 
  selectedVariantIndex = 0 
}) => {
  // State for Payment Estimator
  const [tenureYears, setTenureYears] = useState(7);
  const [dpPercent, setDpPercent] = useState(20);
  
  const variant = variants[selectedVariantIndex];
  if (!variant) return null;

  // Calculations
  const price = variant.priceGroup.currentPrice || 0;
  const downPaymentAmt = price * (dpPercent / 100);
  const loanAmount = price - downPaymentAmt;
  const interestRate = 2.78; // Default estimated interest rate
  const totalInterest = loanAmount * (interestRate / 100) * tenureYears;
  const totalPayable = loanAmount + totalInterest;
  const monthlyInstallment = totalPayable / (tenureYears * 12);

  // Currency Formatter
  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('en-SG', { 
      style: 'currency', 
      currency: 'SGD', 
      maximumFractionDigits: 0 
    }).format(val);

  return (
    <Box sx={{ mt: 3 }}>
      {/* 1. Variants & Pricing Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 4, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#fbfbfb', borderBottom: '1px solid #eee' }}>
          <Typography variant="subtitle1" fontWeight={700}>Variants & Pricing</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Variant</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Transmission</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Power</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Fuel Economy</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((v, idx) => (
                <TableRow 
                  key={v.vehicleVariantId} 
                  sx={{ bgcolor: idx === selectedVariantIndex ? '#fffaf0' : 'inherit' }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={idx === selectedVariantIndex ? 700 : 500}>
                        {v.variantName}
                      </Typography>
                      {idx === selectedVariantIndex && (
                        <Chip 
                          label="POPULAR" 
                          size="small" 
                          sx={{ bgcolor: '#FFEDD5', color: '#EA580C', fontWeight: 700, fontSize: '0.65rem', height: 20 }} 
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{v.transmissionType}</TableCell>
                  <TableCell>
                    {v.specGroup.maximumPowerOutput ? `${v.specGroup.maximumPowerOutput} kW` : '-'}
                  </TableCell>
                  <TableCell>
                    {v.specGroup.fuelConsumption 
                      ? `${v.specGroup.fuelConsumption} ${v.fuelType === 'Electric' ? 'kWh/100km' : 'km/L'}` 
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ color: '#F59E0B', fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatCurrency(v.priceGroup.currentPrice || 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="caption">
            {variant.priceGroup.priceDisclaimer || "All prices are inclusive of COE (if applicable). Price and specifications may vary."}
          </Typography>
        </Box>
      </Paper>

      {/* 2. Payment Estimator & Add-ons */}
      <Grid container spacing={3}>
        
        {/* Left Column: Estimator */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={700} mb={3}>Payment Estimator</Typography>
            
            <Grid container spacing={2} mb={4}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Loan Tenure</InputLabel>
                  <Select 
                    value={tenureYears} 
                    label="Loan Tenure" 
                    onChange={(e) => setTenureYears(Number(e.target.value))}
                    sx={{ borderRadius: 2 }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map(y => <MenuItem key={y} value={y}>{y} Years</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Down Payment</InputLabel>
                  <Select 
                    value={dpPercent} 
                    label="Down Payment" 
                    onChange={(e) => setDpPercent(Number(e.target.value))}
                    sx={{ borderRadius: 2 }}
                  >
                    {[10, 20, 30, 40, 50].map(p => <MenuItem key={p} value={p}>{p}%</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center" gap={4}>
              <Box flex={1} width="100%">
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Vehicle Price</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatCurrency(price)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Down Payment ({dpPercent}%)</Typography>
                    <Typography variant="body2" fontWeight={600}>- {formatCurrency(downPaymentAmt)}</Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>Loan Amount</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatCurrency(loanAmount)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Total Interest (Est. {interestRate}%)</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatCurrency(totalInterest)}</Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight={800}>Total Payable</Typography>
                    <Typography variant="subtitle2" fontWeight={800}>{formatCurrency(totalPayable)}</Typography>
                  </Box>
                </Stack>
              </Box>
              
              <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                <Typography variant="caption" color="text.secondary" mb={0.5}>Estimated Monthly</Typography>
                <Typography variant="h6" fontWeight={800} mb={2}>
                  {formatCurrency(monthlyInstallment)} <Typography component="span" variant="caption" color="text.secondary">/ month</Typography>
                </Typography>
                
                {/* Visual Donut Chart Component using MUI CircularProgress */}
                <Box position="relative" display="inline-flex" alignItems="center" justifyContent="center">
                  <CircularProgress variant="determinate" value={100} size={110} thickness={5} sx={{ color: '#E5E7EB' }} />
                  <CircularProgress 
                    variant="determinate" 
                    value={(loanAmount / price) * 100} 
                    size={110} 
                    thickness={5} 
                    sx={{ color: '#8BA4F9', position: 'absolute', left: 0 }} 
                  />
                  <Box position="absolute" textAlign="center">
                     <Typography variant="caption" display="block" fontWeight={700} fontSize="0.65rem" color="#8BA4F9">
                       Loan {100 - dpPercent}%
                     </Typography>
                     <Typography variant="caption" display="block" fontWeight={700} fontSize="0.65rem" color="text.secondary">
                       DP {dpPercent}%
                     </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            
            <Typography variant="caption" color="text.secondary" display="block" mt={4}>
              These figures are estimates only. Final interest rate and terms are subject to bank approval.
            </Typography>
          </Paper>
        </Grid>

        {/* Right Column: Add-ons */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Included</Typography>
            <List dense disablePadding sx={{ mb: 3 }}>
              {['COE (if applicable)', 'GST', 'Registration Fee', '6 Months Road Tax', 'Number Plates'].map((item) => (
                <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                </ListItem>
              ))}
            </List>

            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Optional Add-ons</Typography>
            <Stack spacing={0.5}>
              {[
                { label: 'Extended Warranty', price: 1298 },
                { label: 'Service Package', price: 1588 },
                { label: 'Commercial Insurance', price: 1200 },
                { label: 'Accessories Package', price: 888 },
              ].map((addon) => (
                <Box key={addon.label} display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <Checkbox size="small" sx={{ p: 0.5, mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">{addon.label}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">${addon.price}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

export default NewVehicleTabPricing;